import type { SearchcraftCore } from '@classes';
import { registry } from '@classes/CoreInstanceRegistry';
import {
  Component,
  Event,
  type EventEmitter,
  Prop,
  State,
  h,
} from '@stencil/core';
import classNames from 'classnames';

/**
 * This web component simulates a light switch functionality, providing a simple and intuitive toggle between two states—on and off.
 *
 * @internal
 */
@Component({
  tag: 'searchcraft-toggle-button',
  shadow: false,
})
export class SearchcraftToggleButton {
  /**
   * The label.
   */
  @Prop() label = 'Toggle';
  /**
   * The secondary label displayed below the main label.
   */
  @Prop() subLabel: string | undefined;
  /**
   * The id of the Searchcraft instance that this component should use.
   */
  @Prop() searchcraftId?: string;

  /**
   * When the toggle element is changed.
   */
  @Event() toggleUpdated?: EventEmitter<boolean>;

  @State() isActive = false;
  @State() lastSearchTerm: string | undefined;
  private unsubscribe: (() => void) | undefined;
  private cleanupCore?: () => void;

  private handleToggle = async () => {
    this.isActive = !this.isActive;
    this.toggleUpdated?.emit(this.isActive);
  };

  onCoreAvailable(core: SearchcraftCore) {
    /** When the query changes, sets toggle button state back to inactive. */
    this.unsubscribe = core.store.subscribe((state) => {
      if (
        state.searchTerm !== this.lastSearchTerm &&
        state.searchTerm.trim().length === 0
      ) {
        this.isActive = false;
      }
      this.lastSearchTerm = state.searchTerm;
    });
  }

  connectedCallback() {
    this.cleanupCore = registry.useCoreInstance(
      this.searchcraftId,
      this.onCoreAvailable.bind(this),
    );
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    this.cleanupCore?.();
  }

  render() {
    return (
      <div
        class={classNames('searchcraft-toggle-button', {
          'searchcraft-toggle-button-active': this.isActive,
        })}
      >
        <div>
          <p class='searchcraft-toggle-button-label'>{this.label}</p>
          {this.subLabel && (
            <p class='searchcraft-toggle-button-sub-label'>{this.subLabel}</p>
          )}
        </div>
        <button
          class='searchcraft-toggle-button-background'
          onClick={this.handleToggle}
          type='button'
        >
          <div class='searchcraft-toggle-button-handle' />
        </button>
      </div>
    );
  }
}
